<script setup lang="ts">
import { nextTick, reactive, ref } from 'vue';
import AppIcon from './AppIcon.vue';

/** One text field with a title and a confirm button, in place of window.prompt. */
export interface TextPrompt {
  readonly title: string;
  readonly label: string;
  readonly value: string;
  readonly confirmLabel: string;
  readonly help?: string;
  readonly maxlength?: number;
}
defineProps<{ error: string; busy?: boolean }>();
const emit = defineEmits<{ confirm: [value: string]; close: [] }>();
const dialog = ref<HTMLDialogElement>();
const input = ref<HTMLInputElement>();
const state = reactive<TextPrompt & { current: string }>({
  title: '',
  label: '',
  value: '',
  confirmLabel: 'Save',
  current: '',
});

async function open(prompt: TextPrompt) {
  Object.assign(state, prompt, { current: prompt.value });
  dialog.value?.showModal();
  await nextTick();
  input.value?.select();
}
function close() {
  dialog.value?.close();
}
function submit() {
  const value = state.current.trim();
  if (!value) return;
  emit('confirm', value);
}
defineExpose({ open, close });
</script>

<template>
  <dialog
    ref="dialog"
    class="dialog dialog-compact"
    aria-labelledby="prompt-title"
    @close="emit('close')"
  >
    <form class="dialog-body" @submit.prevent="submit">
      <header class="dialog-head">
        <h2 id="prompt-title">{{ state.title }}</h2>
        <button type="button" class="icon-button quiet" aria-label="Close" @click="close">
          <AppIcon name="close" />
        </button>
      </header>
      <p v-if="error" class="banner error" role="alert">{{ error }}</p>
      <label class="field">
        <span class="field-label">{{ state.label }}</span>
        <input
          ref="input"
          v-model="state.current"
          required
          :maxlength="state.maxlength ?? 60"
          autocomplete="off"
        />
        <span v-if="state.help" class="field-help">{{ state.help }}</span>
      </label>
      <footer class="dialog-actions">
        <button type="button" @click="close">Cancel</button>
        <button type="submit" class="primary" :disabled="busy">{{ state.confirmLabel }}</button>
      </footer>
    </form>
  </dialog>
</template>
